import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, formatDateBR } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, Clock, Package, DollarSign, Calendar, Truck, Eye, X, ChevronRight, ClipboardCheck, Wifi, WifiOff } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { colors } from '../theme';
import ModalHistoricoPedido from './ModalHistoricoPedido';

// Intervalo de atualização em milissegundos (15 segundos)
const REFRESH_INTERVAL = 15000;

const DashboardNew = ({ user }) => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [allPedidos, setAllPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [updateCount, setUpdateCount] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Usar refs para valores que mudam mas não devem causar re-render do effect
  const intervalRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const lastUpdateDateRef = useRef(null);
  
  // Estado para modal de detalhes
  const [modalOpen, setModalOpen] = useState(false);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
  
  // Estado para modal de lista filtrada
  const [filtroModalOpen, setFiltroModalOpen] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState(null);
  const [pedidosFiltrados, setPedidosFiltrados] = useState([]);
  const [loadingFiltro, setLoadingFiltro] = useState(false);
  
  const abrirDetalhes = (pedido) => {
    setPedidoSelecionado(pedido);
    setModalOpen(true);
  };
  
  const fecharDetalhes = () => {
    setModalOpen(false);
    setPedidoSelecionado(null);
  };

  // Função para abrir modal com pedidos filtrados (busca do backend)
  const abrirFiltro = async (tipo, titulo) => {
    setFiltroAtivo({ tipo, titulo });
    setPedidosFiltrados([]);
    setLoadingFiltro(true);
    setFiltroModalOpen(true);
    
    try {
      const token = localStorage.getItem('primaza_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/dashboard/pedidos-filtrados?tipo=${tipo}`, { headers });
      setPedidosFiltrados(response.data.pedidos || []);
    } catch (error) {
      console.error('Erro ao buscar pedidos filtrados:', error);
      setPedidosFiltrados([]);
    } finally {
      setLoadingFiltro(false);
    }
  };

  const fecharFiltro = () => {
    setFiltroModalOpen(false);
    setFiltroAtivo(null);
    setPedidosFiltrados([]);
  };

  // Função para ir para Conferência Diária com pedido filtrado
  const irParaConferencia = (pedido) => {
    fecharFiltro();
    navigate('/conferencia-diaria', { state: { filtroPedido: pedido.pedido } });
  };

  // Função de carregamento de dados - SEM dependências que mudam
  const loadDashboardData = useCallback(async (showLoading = true, isRetry = false) => {
    if (showLoading) {
      setLoading(true);
    }
    setIsUpdating(true);
    
    try {
      const currentDate = new Date().toDateString();
      const token = localStorage.getItem('primaza_token');
      
      if (!token) {
        console.warn('⚠️ Token não encontrado');
        setIsOnline(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Carregar métricas e pedidos recentes com timeout
      const [metricsRes, pedidosRes] = await Promise.all([
        axios.get(`${API}/dashboard/metrics`, { headers, timeout: 10000 }),
        axios.get(`${API}/dashboard/ultimos-pedidos?limit=10`, { headers, timeout: 10000 })
      ]);
      
      setMetrics(metricsRes.data);
      
      const pedidosData = pedidosRes.data.pedidos || [];
      setAllPedidos(pedidosData);
      setPedidos(pedidosData);
      
      const now = new Date();
      setLastUpdate(now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setUpdateCount(prev => prev + 1);
      setIsOnline(true);
      
      // Detectar mudança de dia usando ref
      if (lastUpdateDateRef.current && lastUpdateDateRef.current !== currentDate) {
        console.log('📅 Novo dia detectado! Dados recarregados.');
      }
      lastUpdateDateRef.current = currentDate;
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      setIsOnline(false);
      
      // Se for erro de autenticação, não tentar retry
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('⚠️ Sessão expirada');
        return;
      }
      
      // Tentar novamente após 5 segundos se não for retry
      if (!isRetry) {
        retryTimeoutRef.current = setTimeout(() => {
          loadDashboardData(false, true);
        }, 5000);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      setTimeout(() => setIsUpdating(false), 500);
    }
  }, []); // SEM dependências - função estável

  // Setup do polling e listeners - Executa apenas UMA VEZ no mount
  useEffect(() => {
    console.log(`🚀 Dashboard iniciado - Atualização automática a cada ${REFRESH_INTERVAL/1000}s`);
    
    // Carregar dados inicial
    loadDashboardData();
    
    // Configurar intervalo de atualização
    intervalRef.current = setInterval(() => {
      loadDashboardData(false);
    }, REFRESH_INTERVAL);
    
    // Listener para quando a aba fica visível novamente
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Aba visível - atualizando dados...');
        loadDashboardData(false);
      }
    };
    
    // Listener para quando a conexão volta
    const handleOnline = () => {
      console.log('📶 Conexão restaurada');
      loadDashboardData(false);
    };
    
    const handleOffline = () => {
      console.log('📵 Conexão perdida');
    };
    
    // Adicionar listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Limpar tudo quando desmontar
    return () => {
      console.log('🛑 Dashboard desmontado - parando polling');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [loadDashboardData]); // loadDashboardData é estável (deps vazias)

  // Funções para filtrar pedidos por categoria (buscam do backend)
  const filtrarEmAtraso = () => {
    abrirFiltro('em_atraso', '🚨 Pedidos em Atraso');
  };

  const filtrarEntregaHoje = () => {
    abrirFiltro('entrega_hoje', '📅 Entrega Prevista para Hoje');
  };

  const filtrarSemAtualizacao = () => {
    abrirFiltro('sem_atualizacao', '⚠️ Sem Atualização (+3 dias)');
  };

  const filtrarNovos24h = () => {
    abrirFiltro('novos_24h', '📦 Novos Pedidos (24h)');
  };

  const filtrarEntregues = () => {
    abrirFiltro('entregues', '✅ Pedidos Entregues');
  };

  const filtrarEmTransito = () => {
    abrirFiltro('em_transito', '🚚 Em Trânsito');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status) => {
    const colors = {
      'ENTREGUE': 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300',
      'FATURADO': 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300',
      'IMPRESSO': 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300',
      'EMBALADO': 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300',
      'ENVIADO PARA PRODUÇÃO': 'bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border border-orange-300'
    };
    return colors[status] || 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border border-gray-300';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'ENTREGUE': '✅',
      'FATURADO': '💰',
      'IMPRESSO': '🖨️',
      'EMBALADO': '📦',
      'ENVIADO PARA PRODUÇÃO': '🏭'
    };
    return icons[status] || '📄';
  };

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" style={{ color: colors.primaza.primary }} />
          <p className="text-lg font-medium text-gray-600">Carregando Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Alerta de conexão perdida */}
      {!isOnline && (
        <div className="bg-red-500 text-white text-center py-2 px-4 text-sm flex items-center justify-center gap-2">
          <WifiOff className="h-4 w-4" />
          Conexão perdida - tentando reconectar...
        </div>
      )}
      
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
              Dashboard
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Visão geral dos pedidos e estatísticas
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Indicador de atualização automática */}
            <Badge 
              variant="outline" 
              className={`hidden lg:inline-flex text-xs ${isOnline ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}
              title={`Atualização automática a cada ${REFRESH_INTERVAL/1000} segundos`}
            >
              <span className={`w-2 h-2 rounded-full mr-2 ${isOnline ? 'bg-green-500' : 'bg-red-500'} ${isUpdating ? 'animate-ping' : 'animate-pulse'}`}></span>
              <span className={isOnline ? 'text-green-700' : 'text-red-700'}>
                {isOnline ? (
                  <>
                    {isUpdating ? '🔄' : '⚡'} Tempo Real ({REFRESH_INTERVAL/1000}s)
                  </>
                ) : (
                  '⚠️ Offline'
                )}
              </span>
            </Badge>
            
            {/* Contador de atualizações (útil para debug em TV) */}
            <Badge variant="outline" className="hidden xl:inline-flex text-xs bg-blue-50 border-blue-300">
              <span className="text-blue-700">#{updateCount}</span>
            </Badge>
            
            {lastUpdate && (
              <Badge variant="outline" className="hidden sm:inline-flex text-xs">
                <Clock className="w-3 h-3 mr-1.5" />
                {lastUpdate}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadDashboardData(true)}
              disabled={loading}
              style={{ 
                borderColor: colors.primaza.primary,
                color: colors.primaza.secondary 
              }}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading || isUpdating ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto p-6 space-y-6">
        {/* SEÇÃO 1: ALERTAS URGENTES */}
        {metrics && (
          <>
            <div>
              <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4 flex items-center" style={{ color: colors.primaza.secondary }}>
                🚨 Atenção Urgente
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                {/* Pedidos em Atraso */}
                <Card 
                  className="border-l-4 border-l-red-500 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02]"
                  onClick={filtrarEmAtraso}
                >
                  <CardHeader className="pb-2 md:pb-3">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center justify-between">
                      <span className="flex items-center">
                        <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2 text-red-500" />
                        <span className="text-sm md:text-base">Em Atraso</span>
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-3xl md:text-4xl font-bold text-red-600">
                      {metrics.alertas_urgentes.em_atraso}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 md:mt-2">
                      Clique para ver detalhes
                    </p>
                  </CardContent>
                </Card>

                {/* Entrega Prevista para Hoje */}
                <Card 
                  className="border-l-4 border-l-orange-500 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02]"
                  onClick={filtrarEntregaHoje}
                >
                  <CardHeader className="pb-2 md:pb-3">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center justify-between">
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2 text-orange-500" />
                        <span className="text-sm md:text-base">Entrega Hoje</span>
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-3xl md:text-4xl font-bold text-orange-600">
                      {metrics.alertas_urgentes.entrega_hoje}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 md:mt-2">
                      Clique para ver detalhes
                    </p>
                  </CardContent>
                </Card>

                {/* Sem Atualização >3 dias */}
                <Card 
                  className="border-l-4 border-l-yellow-500 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02]"
                  onClick={filtrarSemAtualizacao}
                >
                  <CardHeader className="pb-2 md:pb-3">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center justify-between">
                      <span className="flex items-center">
                        <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2 text-yellow-500" />
                        <span className="text-sm md:text-base">Sem Atualização</span>
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-3xl md:text-4xl font-bold text-yellow-600">
                      {metrics.alertas_urgentes.sem_atualizacao}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 md:mt-2">
                      Clique para ver detalhes
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* SEÇÃO 2: NÚMEROS DE HOJE/SEMANA */}
            <div>
              <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4 flex items-center" style={{ color: colors.primaza.secondary }}>
                📊 Hoje & Esta Semana
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {/* Novos Pedidos (24h) */}
                <Card 
                  className="border-l-4 border-l-blue-500 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02]"
                  onClick={filtrarNovos24h}
                >
                  <CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex flex-col md:flex-row md:items-center md:justify-between">
                      <span className="flex items-center">
                        <Package className="h-4 w-4 mb-1 md:mb-0 md:mr-2 text-blue-500" />
                        <span>Novos (24h)</span>
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400 hidden md:block" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-3 md:px-6 pb-3 md:pb-6">
                    <div className="text-2xl md:text-3xl font-bold text-blue-600">
                      {metrics.numeros_atuais.novos_24h}
                    </div>
                  </CardContent>
                </Card>

                {/* Entregas Hoje */}
                <Card 
                  className="border-l-4 border-l-green-500 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02]"
                  onClick={filtrarEntregues}
                >
                  <CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center justify-between">
                      <span>✅ Entregues</span>
                      <ChevronRight className="h-4 w-4 text-gray-400 hidden md:block" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-3 md:px-6 pb-3 md:pb-6">
                    <div className="text-2xl md:text-3xl font-bold text-green-600">
                      {metrics.numeros_atuais.entregas_hoje}
                    </div>
                  </CardContent>
                </Card>

                {/* Em Trânsito */}
                <Card 
                  className="border-l-4 border-l-purple-500 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02]"
                  onClick={filtrarEmTransito}
                >
                  <CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex flex-col md:flex-row md:items-center md:justify-between">
                      <span className="flex items-center">
                        <Truck className="h-4 w-4 mb-1 md:mb-0 md:mr-2 text-purple-500" />
                        <span>Em Trânsito</span>
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400 hidden md:block" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-3 md:px-6 pb-3 md:pb-6">
                    <div className="text-2xl md:text-3xl font-bold text-purple-600">
                      {metrics.numeros_atuais.em_transito}
                    </div>
                  </CardContent>
                </Card>

                {/* Valor em Trânsito */}
                <Card className="border-l-4 border-l-emerald-500 hover:shadow-lg transition-all duration-200">
                  <CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex flex-col md:flex-row md:items-center">
                      <DollarSign className="h-4 w-4 mb-1 md:mb-0 md:mr-2 text-emerald-500" />
                      <span>Valor R$</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-3 md:px-6 pb-3 md:pb-6">
                    <div className="text-lg md:text-2xl font-bold text-emerald-600">
                      {formatCurrency(metrics.numeros_atuais.valor_em_transito)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* SEÇÃO 3: PERFORMANCE DA SEMANA */}
            <div>
              <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4 flex items-center" style={{ color: colors.primaza.secondary }}>
                🎯 Performance da Semana
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
                {/* Taxa de Entrega no Prazo */}
                <Card className="hover:shadow-lg transition-all duration-200">
                  <CardHeader className="pb-2 md:pb-3 px-4 md:px-6 pt-4 md:pt-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600">
                      Taxa Entrega no Prazo
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 md:px-6 pb-4 md:pb-6">
                    <div className="flex items-end justify-between">
                      <div className={`text-2xl md:text-3xl font-bold ${metrics.performance_semana.taxa_entrega_prazo >= 95 ? 'text-green-600' : 'text-red-600'}`}>
                        {metrics.performance_semana.taxa_entrega_prazo}%
                      </div>
                      {metrics.performance_semana.taxa_entrega_prazo >= 95 ? (
                        <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-green-500" />
                      ) : (
                        <TrendingDown className="h-5 w-5 md:h-6 md:w-6 text-red-500" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 md:mt-2">
                      Meta: &gt;95% ({metrics.performance_semana.total_entregas})
                    </p>
                  </CardContent>
                </Card>

                {/* Margem de Frete Média */}
                <Card className="hover:shadow-lg transition-all duration-200">
                  <CardHeader className="pb-2 md:pb-3 px-4 md:px-6 pt-4 md:pt-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600">
                      Margem Frete Média
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 md:px-6 pb-4 md:pb-6">
                    <div className="flex items-end justify-between">
                      <div className={`text-2xl md:text-3xl font-bold ${metrics.performance_semana.margem_frete_media <= 5 ? 'text-green-600' : 'text-red-600'}`}>
                        {metrics.performance_semana.margem_frete_media}%
                      </div>
                      {metrics.performance_semana.margem_frete_media <= 5 ? (
                        <TrendingDown className="h-5 w-5 md:h-6 md:w-6 text-green-500" />
                      ) : (
                        <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-red-500" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 md:mt-2">
                      Meta: &lt;5% do valor NF
                    </p>
                  </CardContent>
                </Card>

                {/* Tempo Médio de Entrega */}
                <Card className="hover:shadow-lg transition-all duration-200">
                  <CardHeader className="pb-2 md:pb-3 px-4 md:px-6 pt-4 md:pt-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600">
                      Tempo Médio Entrega
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 md:px-6 pb-4 md:pb-6">
                    <div className="flex items-end justify-between">
                      <div className="text-2xl md:text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
                        {metrics.performance_semana.tempo_medio_entrega}
                      </div>
                      <Calendar className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 md:mt-2">
                      Dias em média
                    </p>
                  </CardContent>
                </Card>

                {/* Card de Info */}
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 hover:shadow-lg transition-all duration-200">
                  <CardHeader className="pb-2 md:pb-3 px-4 md:px-6 pt-4 md:pt-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-blue-800">
                      ℹ️ Indicadores
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 md:px-6 pb-4 md:pb-6">
                    <div className="space-y-1 text-xs text-blue-700">
                      <div>✅ Taxa: &gt;95%</div>
                      <div>💰 Margem: &lt;5%</div>
                      <div>📦 Semana atual</div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* SEÇÃO 4: TOP TRANSPORTADORAS */}
            <div>
              <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4 flex items-center" style={{ color: colors.primaza.secondary }}>
                🚚 Top 3 Transportadoras
              </h2>
              <Card>
                <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
                  {metrics.top_transportadoras.length > 0 ? (
                    <>
                      {/* Mobile: Cards */}
                      <div className="block md:hidden space-y-3">
                        {metrics.top_transportadoras.map((transp, index) => (
                          <div key={index} className="bg-white border-2 rounded-lg p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-2xl">
                                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                                </span>
                                <span className="font-bold text-base">{transp.nome}</span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-3">
                              <div className="bg-gray-50 rounded p-2 text-center">
                                <div className="text-xs text-gray-500 mb-1">Pedidos</div>
                                <div className="text-lg font-bold text-gray-700">{transp.total_pedidos}</div>
                              </div>
                              <div className="bg-gray-50 rounded p-2 text-center">
                                <div className="text-xs text-gray-500 mb-1">Taxa</div>
                                <div className={`text-lg font-bold ${transp.taxa_entrega_prazo >= 90 ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {transp.taxa_entrega_prazo}%
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop: Tabela */}
                      <div className="hidden md:block overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12 text-center">#</TableHead>
                              <TableHead>Transportadora</TableHead>
                              <TableHead className="text-center">Qtd Pedidos</TableHead>
                              <TableHead className="text-center">Taxa Entrega no Prazo</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {metrics.top_transportadoras.map((transp, index) => (
                              <TableRow key={index} className="hover:bg-gray-50">
                                <TableCell className="text-center font-bold text-lg">
                                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                                </TableCell>
                                <TableCell className="font-medium">{transp.nome}</TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline" className="font-mono">
                                    {transp.total_pedidos}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={transp.taxa_entrega_prazo >= 90 ? 'bg-green-100 text-green-800 border-green-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300'}>
                                    {transp.taxa_entrega_prazo}%
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  ) : (
                    <Alert>
                      <AlertDescription className="text-center text-gray-500 text-sm">
                        📦 Nenhuma transportadora com pedidos esta semana
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* SEÇÃO 5: ÚLTIMOS PEDIDOS */}
        <div>
          <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4 flex items-center" style={{ color: colors.primaza.secondary }}>
            📋 Últimos 10 Pedidos
          </h2>
          <Card>
            <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
              {pedidos.length > 0 ? (
                <>
                  {/* Mobile: Cards */}
                  <div className="block md:hidden space-y-3">
                    {pedidos.map((pedido, index) => (
                      <div key={pedido.id || index} className="bg-white border-2 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-bold text-base">#{pedido.pedido || 'S/N'}</div>
                            {user.role === 'admin' && (
                              <div className="text-xs text-gray-500">{pedido.vendedor || 'N/A'}</div>
                            )}
                          </div>
                          {pedido.status_pedido && (
                            <Badge className={getStatusColor(pedido.status_pedido) + " text-xs px-2 py-0.5"}>
                              <span className="mr-0.5">{getStatusIcon(pedido.status_pedido)}</span>
                              <span className="hidden sm:inline">{pedido.status_pedido}</span>
                            </Badge>
                          )}
                        </div>
                        
                        <div className="space-y-1 text-xs mb-2">
                          <div className="truncate">
                            <span className="font-medium text-gray-600">Cliente:</span> 
                            <span className="ml-1">{pedido.nome_cliente || '-'}</span>
                          </div>
                          {pedido.transportadora && (
                            <div className="truncate">
                              <span className="font-medium text-gray-600">Transportadora:</span> 
                              <span className="ml-1">{pedido.transportadora}</span>
                            </div>
                          )}
                          {pedido.prev_entrega && (
                            <div>
                              <span className="font-medium text-gray-600">Previsão:</span> 
                              <span className="ml-1">{formatDateBR(pedido.prev_entrega)}</span>
                            </div>
                          )}
                        </div>
                        
                        <Button 
                          onClick={() => abrirDetalhes(pedido)}
                          size="sm"
                          variant="outline"
                          className="w-full text-xs"
                          style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Ver Detalhes
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Desktop: Tabela */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Pedido</TableHead>
                          {user.role === 'admin' && <TableHead>Vendedor</TableHead>}
                          <TableHead>Cliente</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Prev. Entrega</TableHead>
                          <TableHead>Transportadora</TableHead>
                          <TableHead className="text-center">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pedidos.map((pedido, index) => (
                          <TableRow key={pedido.id || index} className="hover:bg-gray-50">
                            <TableCell className="font-medium">{pedido.pedido || '-'}</TableCell>
                            {user.role === 'admin' && <TableCell>{pedido.vendedor || '-'}</TableCell>}
                            <TableCell className="max-w-xs truncate" title={pedido.nome_cliente}>
                              {pedido.nome_cliente || '-'}
                            </TableCell>
                            <TableCell>
                              {pedido.status_pedido ? (
                                <Badge className={getStatusColor(pedido.status_pedido)}>
                                  <span className="mr-1">{getStatusIcon(pedido.status_pedido)}</span>
                                  {pedido.status_pedido}
                                </Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell>{formatDateBR(pedido.prev_entrega)}</TableCell>
                            <TableCell>{pedido.transportadora || '-'}</TableCell>
                            <TableCell className="text-center">
                              <Button 
                                onClick={() => abrirDetalhes(pedido)}
                                size="sm"
                                variant="outline"
                                style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Ver Detalhes
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <Alert>
                  <AlertDescription className="text-center text-gray-500">
                    📦 Nenhum pedido encontrado
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Modal de Detalhes do Pedido */}
      {pedidoSelecionado && (
        <ModalHistoricoPedido 
          isOpen={modalOpen}
          onClose={fecharDetalhes}
          pedido={pedidoSelecionado}
        />
      )}

      {/* Modal de Lista Filtrada */}
      <Dialog open={filtroModalOpen} onOpenChange={setFiltroModalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="text-xl font-bold" style={{ color: colors.primaza.secondary }}>
                {filtroAtivo?.titulo || 'Pedidos'}
              </span>
              <Badge variant="outline" className="ml-2">
                {pedidosFiltrados.length} pedidos
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[65vh]">
            {loadingFiltro ? (
              <div className="text-center py-12 text-gray-500">
                <Loader2 className="h-12 w-12 mx-auto mb-4 text-gray-300 animate-spin" />
                <p>Carregando pedidos...</p>
              </div>
            ) : pedidosFiltrados.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhum pedido encontrado</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pedidosFiltrados.map((pedido, index) => (
                  <div 
                    key={pedido.id || index}
                    className="p-3 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-gray-900">#{pedido.pedido || 'S/N'}</span>
                          {pedido.status_pedido && (
                            <Badge className={getStatusColor(pedido.status_pedido) + " text-xs"}>
                              {getStatusIcon(pedido.status_pedido)} {pedido.status_pedido}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 truncate">
                          {pedido.nome_cliente || 'Cliente não informado'}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                          {pedido.transportadora && (
                            <span className="flex items-center">
                              <Truck className="h-3 w-3 mr-1" />
                              {pedido.transportadora}
                            </span>
                          )}
                          {pedido.prev_entrega && (
                            <span className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              Prev: {formatDateBR(pedido.prev_entrega)}
                            </span>
                          )}
                          {user.role === 'admin' && pedido.vendedor && (
                            <span className="text-gray-400">
                              Vendedor: {pedido.vendedor}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Botões de Ação */}
                      <div className="flex gap-2 flex-shrink-0 ml-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            fecharFiltro();
                            abrirDetalhes(pedido);
                          }}
                          className="text-xs"
                          style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Histórico
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => irParaConferencia(pedido)}
                          className="text-xs"
                          style={{ backgroundColor: colors.primaza.primary }}
                        >
                          <ClipboardCheck className="h-3 w-3 mr-1" />
                          Conferência
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={fecharFiltro}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardNew;
